import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { IdCard } from "lucide-react";
import { toast } from "sonner";
import { PasskeySignInButton } from "@/components/PasskeySignInButton";

function PinBoxes({ value, onChange, disabled }) {
  const refs = [useRef(null), useRef(null), useRef(null), useRef(null)];
  const digits = value.padEnd(4, " ").slice(0, 4).split("");

  const setDigit = (i, v) => {
    const clean = v.replace(/\D/g, "").slice(-1);
    const next = digits.map((d) => (d === " " ? "" : d));
    next[i] = clean;
    const joined = next.join("").slice(0, 4);
    onChange(joined);
    if (clean && i < 3) refs[i + 1].current?.focus();
  };

  const onKeyDown = (i, e) => {
    if (e.key === "Backspace" && !digits[i].trim() && i > 0) {
      refs[i - 1].current?.focus();
    }
  };

  return (
    <div className="flex gap-3 justify-center" data-testid="staff-pin-boxes">
      {[0, 1, 2, 3].map((i) => (
        <input
          key={`pin-${i}`}
          ref={refs[i]}
          type="password"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={1}
          value={digits[i].trim()}
          onChange={(e) => setDigit(i, e.target.value)}
          onKeyDown={(e) => onKeyDown(i, e)}
          disabled={disabled}
          className="w-14 h-16 text-center text-3xl font-bold rounded-xl border-2 border-gray-200 bg-white text-[#1A2744] focus:border-[#C9A84C] focus:outline-none focus:ring-2 focus:ring-[#C9A84C]/40 transition-colors"
          data-testid={`staff-pin-${i}`}
        />
      ))}
    </div>
  );
}

export function BrandHeader() {
  return (
    <div className="flex items-center gap-4">
      <img
        src="/logo.png"
        alt="Coffee Lovers Café"
        className="h-16 w-16 sm:h-20 sm:w-20 rounded-2xl object-cover shadow-md"
      />
      <div>
        <div className="font-logo text-4xl sm:text-5xl font-semibold text-[#1A2744] leading-none">
          Coffee Lovers Café
        </div>
        <div className="mt-1.5 text-[11px] sm:text-xs tracking-[0.3em] uppercase text-[#C9A84C] font-semibold">
          Staff Portal
        </div>
      </div>
    </div>
  );
}

export default function Login() {
  const { user, loginStaff } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [empCode, setEmpCode] = useState("");
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      const dest =
        location.state?.from?.pathname ||
        (user.role === "EMPLOYEE" ? "/clock" : "/admin");
      navigate(dest, { replace: true });
    }
  }, [user, navigate, location]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (pin.length !== 4) {
      toast.error("Please enter your 4-digit PIN");
      return;
    }
    setLoading(true);
    try {
      const u = await loginStaff(empCode.trim().toUpperCase(), pin);
      toast.success(`Kia ora, ${u.name.split(" ")[0]}`);
      navigate("/clock", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign-in failed");
      setPin("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex flex-col">
      <div className="px-6 pt-8 sm:px-12 sm:pt-12">
        <BrandHeader />
      </div>

      <div className="flex-1 flex items-center justify-center px-6 pb-10">
        <div className="w-full max-w-md">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#1A2744] tracking-tight">
            Welcome
          </h2>
          <p className="mt-2 text-gray-500">
            Enter your employee code and your 4-digit PIN.
          </p>

          <form onSubmit={onSubmit} className="mt-7 space-y-6" data-testid="staff-login-form">
            <div>
              <Label htmlFor="empcode" className="text-[#1A2744]">Employee code</Label>
              <div className="relative mt-1.5">
                <IdCard className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="empcode"
                  autoComplete="username"
                  required
                  value={empCode}
                  onChange={(e) => setEmpCode(e.target.value.toUpperCase())}
                  placeholder="e.g. CHCH001"
                  className="pl-10 h-12 tracking-wider font-medium"
                  data-testid="staff-employee-code"
                />
              </div>
            </div>
            <div>
              <Label className="text-[#1A2744] block mb-2">4-digit PIN</Label>
              <PinBoxes value={pin} onChange={setPin} disabled={loading} />
            </div>
            <Button
              type="submit"
              disabled={loading || pin.length !== 4}
              className="w-full h-12 text-base bg-[#1A2744] hover:bg-[#263A63] text-white font-semibold"
              data-testid="staff-login-submit"
            >
              {loading ? "Signing in…" : "Sign in"}
            </Button>

            <PasskeySignInButton defaultLandingPath="/clock" />

            <p className="text-xs text-gray-400 text-center">
              Forgot your PIN? Ask your manager to reset it.
            </p>
          </form>

          <div className="mt-8 p-4 rounded-xl border border-dashed border-[#C9A84C]/40 bg-[#C9A84C]/5 text-xs text-[#1A2744]/80">
            <div className="font-semibold text-[#1A2744] mb-1">First time signing in?</div>
            <div>Your manager will share your employee code (e.g. <span className="font-mono">CHCH001</span> · <span className="font-mono">WLG001</span>) and a 4-digit PIN.</div>
          </div>

          <div className="mt-6 text-center">
            <Link
              to="/admin/login"
              className="text-xs text-gray-500 hover:text-[#1A2744] underline underline-offset-4"
              data-testid="link-manager-login"
            >
              Managers sign in here →
            </Link>
          </div>
        </div>
      </div>
      <footer className="mt-10 text-center text-[11px] text-gray-500" data-testid="login-footer">
        Developed and designed by{" "}
        <span className="font-semibold text-[#1A2744]">Carattere Technologies Limited</span>
      </footer>
    </div>
  );
}
